import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../../../config/api';
import { apiGet, apiRequest } from '../../../services/api';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import AdminModal from '../components/AdminModal';
import AdminPagination from '../components/AdminPagination';
import { useToast } from '../context/ToastContext';

const PAGE_SIZE = 30;

const TIPOS_RECORD = [
  { value: 'squat', label: 'Squat' },
  { value: 'bench_press', label: 'Bench Press' },
  { value: 'deadlift', label: 'Deadlift' },
  { value: 'total', label: 'Total' }
];

const CATEGORIAS_EDAD = [
  { id: 'subjunior', nombre: 'Sub-Junior' },
  { id: 'junior', nombre: 'Junior' },
  { id: 'open', nombre: 'Open' },
  { id: 'master_i', nombre: 'Master I' },
  { id: 'master_ii', nombre: 'Master II' },
  { id: 'master_iii', nombre: 'Master III' },
  { id: 'master_iv', nombre: 'Master IV' },
  { id: 'master_v', nombre: 'Master V' },
];

const labelTipo = (t) => {
  if (!t) return '—';
  const s = String(t).toLowerCase();
  if (s === 'squat') return 'Squat';
  if (s === 'bench_press') return 'Bench Press';
  if (s === 'deadlift') return 'Deadlift';
  if (s === 'total') return 'Total';
  return t;
};

const sortKeyPeso = (peso) => {
  if (peso == null || typeof peso !== 'string') return 0;
  const s = String(peso).trim().toLowerCase();
  const hasta = s.match(/hasta\s*(\d+\.?\d*)/);
  if (hasta) return parseFloat(hasta[1]) || 0;
  const plus = s.match(/\+\s*(\d+\.?\d*)/) || s.match(/(\d+\.?\d*)\s*kg/);
  if (plus) return parseFloat(plus[1]) || 0;
  const num = s.match(/(\d+\.?\d*)/);
  return num ? parseFloat(num[1]) : 0;
};

const sortKeyTipoRecord = (tipo) => {
  const t = (tipo ?? '').toString().trim().toLowerCase();
  if (t === 'squat' || t === 'sq') return 1;
  if (t === 'bench_press' || t === 'bp') return 2;
  if (t === 'deadlift' || t === 'dl') return 3;
  if (t === 'total') return 4;
  return 0;
};

const formatValorKg = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  if (Number.isInteger(n)) return `${n} kg`;
  return `${Number(n).toFixed(1)} kg`;
};

// Normaliza para comparar: "90 kg" ≠ "+90 kg", "140 kg" ≠ "+140 kg". Solo + explícito → categoría abierta.
const normPesoParaMatch = (v) => {
  if (v == null) return '';
  const norm = (x) => String(x).trim().replace(/\s+/g, ' ').replace(',', '.');
  const raw = norm(v);
  let s = raw.toLowerCase().replace(/\s*kg\s*/gi, ' ').trim();
  const hasta = s.match(/hasta\s*(\d+\.?\d*)/);
  if (hasta) {
    const n = parseFloat(hasta[1]);
    return (Number.isInteger(n) ? String(Math.round(n)) : String(n)) + 'kg';
  }
  const tienePlus = raw.includes('+');
  if (tienePlus) {
    const numMatch = s.replace(/\+/g, ' ').trim().match(/(\d+\.?\d*)/);
    if (numMatch) {
      const num = parseFloat(numMatch[1]);
      return `+${Number.isInteger(num) ? Math.round(num) : num}kg`;
    }
  }
  const numMatch = s.match(/^(\d+\.?\d*)/);
  if (numMatch) {
    const num = parseFloat(numMatch[1]);
    return (Number.isInteger(num) ? String(Math.round(num)) : String(num)) + 'kg';
  }
  return s.replace(/\s/g, '') || '';
};

// Mostrar categoría de peso como -90 / 90+ (Mujeres) y 90 / -140 / 140+ (Hombres). Recibe sexo para distinguir.
const formatPesoDisplay = (peso, sexo) => {
  if (peso == null || String(peso).trim() === '' || String(peso).trim() === '—') return '—';
  const s = String(peso).trim().toLowerCase();
  const esHombres = sexo === 'masculino' || (String(sexo || '').toLowerCase().includes('varon') || String(sexo || '').toLowerCase().includes('masc'));
  if (/\+/.test(s) && /\b90\b/.test(s)) return '90+';
  if (/\b90\b/.test(s) && !/\+/.test(s)) return esHombres ? '90' : '-90';
  if (/\+/.test(s) && /\b140\b/.test(s)) return '140+';
  if (/\b140\b/.test(s) && !/\+/.test(s)) return '-140';
  return peso;
};

// Categorías oficiales Mujeres: 44, 48, 52, 56, 60, 67.5, 75, 82.5, 90, +90
const RANGOS_PESO = {
  masculino: ['52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '100 kg', '110 kg', '125 kg', '140 kg', '+140 kg'],
  femenino: ['44 kg', '48 kg', '52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '+90 kg']
};

const sortKeyPesoOption = (s) => {
  if (!s) return -1;
  const str = String(s).trim().toLowerCase();
  const hasta = str.match(/hasta\s*(\d+\.?\d*)/);
  const num = hasta ? parseFloat(hasta[1]) : (str.match(/(\d+\.?\d*)/) || [])[1];
  if (num != null) return parseFloat(num);
  if (str.startsWith('+')) return 500 + parseFloat((str.match(/(\d+\.?\d*)/) || [])[1] || 0);
  return 0;
};

const normalizarCategoriaPesoParaLista = (p) => {
  const s = String(p).trim();
  const hasta = s.match(/hasta\s*(\d+\.?\d*)\s*kg/i);
  if (hasta) return hasta[1] + ' kg';
  return s.replace(/\s+/g, ' ').replace(',', '.');
};

const normModalidad = (v) => {
  const s = (v == null ? '' : String(v).trim().toLowerCase()).replace(/-/g, '_');
  if (s === 'equipado' || s === 'classic_raw' || s === 'classicraw') return 'classic_raw';
  return s || 'raw';
};

const RecordsView = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filtroPeso, setFiltroPeso] = useState('');
  const [filtroSexo, setFiltroSexo] = useState('');
  const [filtroEdad, setFiltroEdad] = useState('');
  const [filtroTipoCompetencia, setFiltroTipoCompetencia] = useState('');
  const [filtroModalidad, setFiltroModalidad] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [formData, setFormData] = useState({
    nombre_atleta: '',
    tipo_record: 'squat',
    valor: '',
    fecha_record: '',
    lugar: '',
    tipo_competencia: 'untested',
    division: 'Masculino',
    categoria_peso: '83 kg',
    categoria_edad: 'open',
    modalidad: 'raw'
  });
  const { showToast } = useToast();
  const navigate = useNavigate();

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet(API_CONFIG.ENDPOINTS.RECORDS.LISTAR);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.records ?? data?.items ?? []) || [];
      const raw = Array.isArray(list) ? list : [];
      setItems(raw.map((r) => ({
        ...r,
        peso: r.peso ?? r.categoria_peso ?? '—',
        sexo: r.sexo === 'femenino' || (r.division && String(r.division).toLowerCase().includes('fem')) ? 'femenino' : 'masculino'
      })));
    } catch (err) {
      console.error('Error al cargar récords:', err);
      setError(err.message || 'Error al cargar récords');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    setPage(1);
  }, [busqueda, filtroPeso, filtroSexo, filtroEdad, filtroTipoCompetencia, filtroModalidad]);

  // Categorías de peso en los datos (normalizadas: "hasta 43 kg" → "43 kg")
  const categoriasPesoEnDatos = useMemo(() => {
    const set = new Set();
    items.forEach((r) => {
      const p = r.peso ?? r.categoria_peso;
      if (p && String(p).trim() && String(p).trim() !== '—') set.add(normalizarCategoriaPesoParaLista(String(p).trim()));
    });
    return Array.from(set).sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b));
  }, [items]);

  // Opciones de peso: oficiales + las que vengan en datos, ordenadas (para filtro y para formulario)
  const opcionesPeso = useMemo(() => {
    const base = [...new Set([...RANGOS_PESO.masculino, ...RANGOS_PESO.femenino])];
    const normKey = (v) => normalizarCategoriaPesoParaLista(v).toLowerCase().replace(/\s+/g, ' ');
    const baseKeys = new Set(base.map((x) => normKey(x)));
    const fromData = categoriasPesoEnDatos.filter((p) => p && !baseKeys.has(normKey(p)));
    const seen = new Set();
    const resto = [...base, ...fromData].filter((x) => {
      const k = normKey(x);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    resto.sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b));
    return resto;
  }, [categoriasPesoEnDatos]);

  // Para agrupar en el desplegable del filtro (Hombres / Mujeres / Otras)
  const opcionesPesoPorSexo = useMemo(() => ({
    masculino: [...RANGOS_PESO.masculino].sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b)),
    femenino: [...RANGOS_PESO.femenino].sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b)),
  }), []);

  const otrasCategoriasPeso = useMemo(() => opcionesPeso.filter((p) => !opcionesPesoPorSexo.masculino.includes(p) && !opcionesPesoPorSexo.femenino.includes(p)), [opcionesPeso, opcionesPesoPorSexo]);

  const modalidadesEnDatos = useMemo(() => {
    const set = new Set();
    items.forEach((r) => {
      const m = r.modalidad;
      if (m != null && String(m).trim()) set.add(String(m).trim().toLowerCase());
    });
    return Array.from(set);
  }, [items]);

  const resetearFiltros = () => {
    setBusqueda('');
    setFiltroPeso('');
    setFiltroSexo('');
    setFiltroEdad('');
    setFiltroTipoCompetencia('');
    setFiltroModalidad('');
    setPage(1);
  };

  const itemsFiltradosYOrdenados = useMemo(() => {
    let list = [...items];
    const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());
    const sexoRecord = (r) => norm(r.sexo ?? r.division);
    const q = busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const nombre = norm(r.nombre);
        const lugar = norm(r.lugar);
        const peso = norm(r.peso);
        return nombre.includes(q) || lugar.includes(q) || peso.includes(q);
      });
    }
    // Filtro por categoría de peso: si el valor es "peso|sexo" (ej. "52 kg|masculino"), filtrar por peso Y sexo
    if (filtroPeso) {
      const tieneSexo = filtroPeso.includes('|');
      if (tieneSexo) {
        const [pesoPart, sexoPart] = filtroPeso.split('|').map((x) => x.trim());
        const pesoNorm = normPesoParaMatch(pesoPart);
        const sexoNorm = norm(sexoPart);
        list = list.filter((r) => normPesoParaMatch(r.peso ?? r.categoria_peso) === pesoNorm && sexoRecord(r) === sexoNorm);
      } else {
        list = list.filter((r) => normPesoParaMatch(r.peso ?? r.categoria_peso) === normPesoParaMatch(filtroPeso));
      }
    }
    if (filtroSexo) list = list.filter((r) => sexoRecord(r) === norm(filtroSexo));
    if (filtroEdad) list = list.filter((r) => norm(r.edad) === norm(filtroEdad));
    if (filtroTipoCompetencia) {
      list = list.filter((r) => {
        const tc = norm(r.tipoCompetencia ?? r.tipo_competencia);
        return tc === norm(filtroTipoCompetencia);
      });
    }
    if (filtroModalidad) list = list.filter((r) => normModalidad(r.modalidad) === normModalidad(filtroModalidad));
    list.sort((a, b) => {
      const pesoA = sortKeyPeso(a.peso);
      const pesoB = sortKeyPeso(b.peso);
      if (pesoA !== pesoB) return pesoA - pesoB;
      const tipoA = sortKeyTipoRecord(a.tipo_record);
      const tipoB = sortKeyTipoRecord(b.tipo_record);
      if (tipoA !== tipoB) return tipoA - tipoB;
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
    return list;
  }, [items, busqueda, filtroPeso, filtroSexo, filtroEdad, filtroTipoCompetencia, filtroModalidad]);

  const totalRecords = itemsFiltradosYOrdenados.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const itemsPagina = useMemo(() => {
    const inicio = (page - 1) * PAGE_SIZE;
    return itemsFiltradosYOrdenados.slice(inicio, inicio + PAGE_SIZE);
  }, [itemsFiltradosYOrdenados, page]);

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    const fechaRaw = record.fecha_record ?? record.fecha ?? '';
    const fechaValue = /^\d{4}-\d{2}-\d{2}/.test(String(fechaRaw)) ? String(fechaRaw).slice(0, 10) : String(fechaRaw || '');
    setFormData({
      nombre_atleta: record.nombre || '',
      tipo_record: (record.tipo_record || 'squat').toLowerCase(),
      valor: record.valor != null ? (Number(record.valor) === Math.round(Number(record.valor)) ? String(Math.round(Number(record.valor))) : String(Number(record.valor))) : '',
      fecha_record: fechaValue,
      lugar: record.lugar || '',
      tipo_competencia: (record.tipoCompetencia || record.tipo_competencia || 'untested').toLowerCase(),
      division: record.sexo === 'femenino' ? 'Femenino' : 'Masculino',
      categoria_peso: normalizarCategoriaPesoParaLista(record.peso || '83 kg') || '83 kg',
      categoria_edad: record.edad || record.categoria_edad || 'open',
      modalidad: normModalidad(record.modalidad) === 'classic_raw' ? 'classic_raw' : 'raw'
    });
    setShowModal(true);
  };

  const handleOpenCreate = () => {
    setEditingRecord(null);
    setFormData({
      nombre_atleta: '',
      tipo_record: 'squat',
      valor: '',
      fecha_record: '',
      lugar: '',
      tipo_competencia: 'untested',
      division: 'Masculino',
      categoria_peso: '83 kg',
      categoria_edad: 'open',
      modalidad: 'raw'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData({ nombre_atleta: '', tipo_record: '', valor: '', fecha_record: '', lugar: '', tipo_competencia: '', division: '', categoria_peso: '', categoria_edad: '', modalidad: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Valor del select "Categoría de peso" en el modal: peso|sexo para Hombres/Mujeres (evita duplicados y sincroniza con división)
  const valueCategoriaPesoSelect = useMemo(() => {
    const p = (formData.categoria_peso || '').trim();
    if (!p) return '';
    const inM = opcionesPesoPorSexo.masculino.includes(p);
    const inF = opcionesPesoPorSexo.femenino.includes(p);
    if (inM && !inF) return `${p}|masculino`;
    if (inF && !inM) return `${p}|femenino`;
    if (inM && inF) return `${p}|${formData.division === 'Femenino' ? 'femenino' : 'masculino'}`;
    return p;
  }, [formData.categoria_peso, formData.division, opcionesPesoPorSexo]);

  const handleCategoriaPesoChange = (e) => {
    const value = e.target.value;
    if (value.includes('|')) {
      const [pesoPart, sexoPart] = value.split('|').map((x) => x.trim());
      setFormData((prev) => ({
        ...prev,
        categoria_peso: pesoPart || prev.categoria_peso,
        division: (sexoPart || '').toLowerCase() === 'femenino' ? 'Femenino' : 'Masculino'
      }));
    } else {
      setFormData((prev) => ({ ...prev, categoria_peso: value || prev.categoria_peso }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        nombre_atleta: formData.nombre_atleta.trim() || null,
        tipo_record: (formData.tipo_record || '').trim().toLowerCase(),
        valor: formData.valor ? (() => { const n = parseFloat(String(formData.valor).replace(',', '.')); return Number.isNaN(n) ? null : Math.round(n * 10) / 10; })() : null,
        fecha_record: formData.fecha_record || null,
        lugar: (formData.lugar || '').trim() || null,
        tipo_competencia: (formData.tipo_competencia || '').trim().toLowerCase() === 'tested' ? 'tested' : 'untested',
        division: formData.division,
        categoria_peso: (formData.categoria_peso || '').trim(),
        categoria_edad: (formData.categoria_edad || '').trim(),
        modalidad: (formData.modalidad || '').trim()
      };
      if (editingRecord) {
        const res = await apiRequest(API_CONFIG.ENDPOINTS.RECORDS.ACTUALIZAR(editingRecord.id), {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Error ${res.status}`);
        }
        showToast('Récord actualizado correctamente', 'success');
      } else {
        if (!(formData.nombre_atleta || '').trim()) {
          throw new Error('El nombre del atleta es obligatorio');
        }
        if (!(formData.division || '').trim() || !['Masculino', 'Femenino'].includes(String(formData.division).trim())) {
          throw new Error('Debe elegir una división (Hombres o Mujeres)');
        }
        if (!(formData.categoria_peso || '').trim()) {
          throw new Error('Debe elegir una categoría de peso');
        }
        const edadVal = (formData.categoria_edad || '').trim();
        if (!edadVal || !CATEGORIAS_EDAD.some((c) => c.id === edadVal)) {
          throw new Error('Debe elegir una categoría de edad');
        }
        if (!(formData.modalidad || '').trim()) {
          throw new Error('Debe elegir una modalidad');
        }
        if (!(formData.tipo_record || '').trim() || !TIPOS_RECORD.some((t) => t.value === (formData.tipo_record || '').trim().toLowerCase())) {
          throw new Error('Debe elegir un tipo de récord');
        }
        if (!payload.valor || payload.valor <= 0 || !payload.fecha_record) {
          throw new Error('El valor (kg) y la fecha son obligatorios');
        }
        if (!(formData.lugar || '').trim()) {
          throw new Error('El lugar es obligatorio');
        }
        const tc = (formData.tipo_competencia || '').trim().toLowerCase();
        if (!tc || !['tested', 'untested'].includes(tc)) {
          throw new Error('Debe elegir un tipo de competencia');
        }
        const res = await apiRequest(API_CONFIG.ENDPOINTS.RECORDS.CREAR, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || errData.detail || `Error ${res.status}`);
        }
        showToast('Récord creado correctamente', 'success');
      }
      handleCloseModal();
      fetchRecords();
    } catch (err) {
      setError(err.message || 'Error al guardar');
      showToast(err.message || 'Error al guardar', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div style={theme.container}>
        <h1 style={theme.title}>Récords</h1>
        <p style={theme.subtitle}>Gestionar récords: completar nombres, tipo, kg y fechas. Agregar récords Untested o Tested.</p>
        <AdminSpinner label="Cargando récords..." />
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={{ ...theme.pageHeader, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={theme.title}>Récords</h1>
          <p style={theme.subtitle}>
            Orden por categoría de peso. Filtrar por categoría, buscar por nombre/lugar. Editar o agregar récords (Untested o Tested).
          </p>
        </div>
        <button
          type="button"
          style={{ ...theme.btnPrimary, whiteSpace: 'nowrap', flexShrink: 0 }}
          onClick={() => navigate('/admin?view=records&sincronizar=liftingcast')}
        >
          Sincronizar récords con LiftingCast
        </button>
      </div>

      {error && <div style={theme.error}>{error}</div>}

      <div style={{ ...theme.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Buscar por nombre, lugar o peso..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ ...theme.input, minWidth: 220, maxWidth: 280 }}
          />
          <select
            value={filtroPeso}
            onChange={(e) => setFiltroPeso(e.target.value)}
            style={{ ...theme.input, minWidth: 160 }}
            aria-label="Filtrar por categoría de peso"
            title="Categoría de peso (kg)"
          >
            <option value="">Todas las categorías de peso</option>
            <optgroup label="Hombres">
              {opcionesPesoPorSexo.masculino.map((p) => (
                <option key={`${p}-m`} value={`${p}|masculino`}>{p}</option>
              ))}
            </optgroup>
            <optgroup label="Mujeres">
              {opcionesPesoPorSexo.femenino.map((p) => (
                <option key={`${p}-f`} value={`${p}|femenino`}>{p}</option>
              ))}
            </optgroup>
            {otrasCategoriasPeso.length > 0 && (
              <optgroup label="Otras (en datos)">
                {otrasCategoriasPeso.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </optgroup>
            )}
          </select>
          <select
            value={filtroSexo}
            onChange={(e) => setFiltroSexo(e.target.value)}
            style={{ ...theme.input, minWidth: 100 }}
          >
            <option value="">Sexo</option>
            <option value="masculino">Hombres</option>
            <option value="femenino">Mujeres</option>
          </select>
          <select
            value={filtroEdad}
            onChange={(e) => setFiltroEdad(e.target.value)}
            style={{ ...theme.input, minWidth: 100 }}
          >
            <option value="">Categoría</option>
            {CATEGORIAS_EDAD.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
          <select
            value={filtroTipoCompetencia}
            onChange={(e) => setFiltroTipoCompetencia(e.target.value)}
            style={{ ...theme.input, minWidth: 110 }}
          >
            <option value="">Tipo competencia</option>
            <option value="untested">Untested</option>
            <option value="tested">Tested</option>
          </select>
          <select
            value={filtroModalidad}
            onChange={(e) => setFiltroModalidad(e.target.value)}
            style={{ ...theme.input, minWidth: 120 }}
          >
            <option value="">Modalidad</option>
            <option value="raw">Raw</option>
            <option value="classic_raw">Classic Raw</option>
            {[...modalidadesEnDatos]
              .filter((m) => normModalidad(m) !== 'raw' && normModalidad(m) !== 'classic_raw')
              .map((m) => (
                <option key={m} value={m}>{m === 'squat' ? 'Only Squat' : m === 'bench' ? 'Only Bench' : m === 'deadlift' ? 'Only Deadlift' : m}</option>
              ))}
          </select>
          <button type="button" style={theme.btnSecondary} onClick={resetearFiltros}>
            Restablecer filtros
          </button>
          <button type="button" style={theme.btnPrimary} onClick={handleOpenCreate}>
            + Agregar récord
          </button>
        </div>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
          Mostrando {itemsPagina.length} en esta página ({totalRecords} récords en total). Orden: categoría de peso → tipo → nombre.
        </p>
        {totalPages > 1 && (
          <AdminPagination
            page={page}
            totalPages={totalPages}
            total={totalRecords}
            pageSize={PAGE_SIZE}
            hasPrev={page > 1}
            hasNext={page < totalPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            label="récords"
          />
        )}
      </div>

      <div style={theme.card}>
        {items.length === 0 ? (
          <p style={theme.empty}>No hay récords. Usa &quot;Agregar récord&quot; para crear uno (Untested o Tested).</p>
        ) : itemsFiltradosYOrdenados.length === 0 ? (
          <p style={theme.empty}>Ningún récord coincide con los filtros o la búsqueda.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>Categoría</th>
                  <th style={theme.th}>Tipo</th>
                  <th style={theme.th}>Atleta</th>
                  <th style={theme.th}>Valor (kg)</th>
                  <th style={theme.th}>Lugar</th>
                  <th style={theme.th}>Fecha</th>
                  <th style={theme.th}>Tipo competencia</th>
                  <th style={theme.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {itemsPagina.map((r) => (
                  <tr key={r.id}>
                    <td style={theme.td}>
                      <span style={{ marginRight: 6 }}>{formatPesoDisplay(r.peso ?? r.categoria_peso, r.sexo)}</span>
                      <span style={{ marginRight: 6, color: '#666' }}>{r.sexo === 'femenino' ? 'Mujeres' : 'Hombres'}</span>
                      <span style={{ color: '#1a4d8c' }}>{CATEGORIAS_EDAD.find((c) => c.id === r.edad)?.nombre || r.edad}</span>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{normModalidad(r.modalidad) === 'classic_raw' ? 'Classic Raw' : r.modalidad === 'raw' ? 'Raw' : (r.modalidad === 'squat' ? 'Only Squat' : r.modalidad === 'bench' ? 'Only Bench' : r.modalidad === 'deadlift' ? 'Only Deadlift' : (r.modalidad || '—'))}</div>
                    </td>
                    <td style={theme.td}>{labelTipo(r.tipo_record)}</td>
                    <td style={theme.td}>{r.nombre || '—'}</td>
                    <td style={theme.td}>{formatValorKg(r.valor)}</td>
                    <td style={theme.td}>{r.lugar || '—'}</td>
                    <td style={theme.td}>{r.fecha ? new Date(r.fecha).toLocaleDateString('es-ES') : '—'}</td>
                    <td style={theme.td}>{((r.tipoCompetencia || r.tipo_competencia) || 'untested').toLowerCase() === 'tested' ? 'Tested' : 'Untested'}</td>
                    <td style={theme.td}>
                      <button type="button" style={theme.btnSecondary} onClick={() => handleOpenEdit(r)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <AdminModal
          open
          onClose={handleCloseModal}
          title={editingRecord ? 'Editar récord' : 'Agregar récord'}
          width={500}
        >
          <form onSubmit={handleSubmit}>
            <div style={theme.formGroup}>
              <label style={theme.label}>Nombre del atleta *</label>
              <input
                type="text"
                name="nombre_atleta"
                value={formData.nombre_atleta}
                onChange={handleInputChange}
                placeholder="Ej. Juan Pérez"
                style={theme.input}
                required={!editingRecord}
              />
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>División (sexo)</label>
              <select name="division" value={formData.division} onChange={handleInputChange} style={theme.input}>
                <option value="">Elegir división</option>
                <option value="Masculino">Hombres</option>
                <option value="Femenino">Mujeres</option>
              </select>
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Categoría de peso</label>
              <select
                name="categoria_peso"
                value={valueCategoriaPesoSelect}
                onChange={handleCategoriaPesoChange}
                style={theme.input}
                aria-label="Categoría de peso del récord"
              >
                <option value="">Elegir categoría de peso</option>
                <optgroup label="Hombres">
                  {opcionesPesoPorSexo.masculino.map((p) => (
                    <option key={`${p}-m`} value={`${p}|masculino`}>{p}</option>
                  ))}
                </optgroup>
                <optgroup label="Mujeres">
                  {opcionesPesoPorSexo.femenino.map((p) => (
                    <option key={`${p}-f`} value={`${p}|femenino`}>{p}</option>
                  ))}
                </optgroup>
                {otrasCategoriasPeso.length > 0 && (
                  <optgroup label="Otras">
                    {otrasCategoriasPeso.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {opcionesPeso.length === 0 && (
                <span style={{ fontSize: 12, color: '#888', display: 'block', marginTop: 4 }}>
                  No hay categorías cargadas. Importe récords desde Excel para que aparezcan aquí.
                </span>
              )}
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Categoría de edad</label>
              <select name="categoria_edad" value={formData.categoria_edad} onChange={handleInputChange} style={theme.input}>
                <option value="">Elegir categoría de edad</option>
                {CATEGORIAS_EDAD.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Modalidad</label>
              <select name="modalidad" value={formData.modalidad} onChange={handleInputChange} style={theme.input}>
                <option value="">Elegir Modalidad</option>
                <option value="raw">Raw</option>
                <option value="classic_raw">Classic Raw</option>
              </select>
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Tipo (SQ / BP / DL / Total)</label>
              <select name="tipo_record" value={formData.tipo_record} onChange={handleInputChange} style={theme.input}>
                <option value="">Elegir tipo de record</option>
                {TIPOS_RECORD.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Valor (kg) *</label>
              <input
                type="number"
                name="valor"
                value={formData.valor}
                onChange={handleInputChange}
                placeholder="Ej. 165"
                step="0.5"
                min="0"
                style={theme.input}
                required={!editingRecord}
              />
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Fecha (mes y año) *</label>
              <input
                type="text"
                name="fecha_record"
                value={formData.fecha_record}
                onChange={handleInputChange}
                placeholder="Ej. Marzo 2022"
                style={theme.input}
                required={!editingRecord}
              />
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Lugar *</label>
              <input
                type="text"
                name="lugar"
                value={formData.lugar}
                onChange={handleInputChange}
                placeholder="Ej. Quirihue, Chile o Ibero IPL, Chile"
                style={theme.input}
                required={!editingRecord}
              />
            </div>
            <div style={theme.formGroup}>
              <label style={theme.label}>Tipo de competencia</label>
              <select name="tipo_competencia" value={formData.tipo_competencia} onChange={handleInputChange} style={theme.input}>
                <option value="">Elegir tipo de competencia</option>
                <option value="untested">Untested</option>
                <option value="tested">Tested</option>
              </select>
              <span style={{ fontSize: 12, color: '#666', marginTop: 4, display: 'block' }}>
                {editingRecord
                  ? 'Marcar como Tested cuando el récord sea con control antidopaje.'
                  : 'Elegir Tested si el récord ya está homologado con control; si no, dejar Untested.'}
              </span>
            </div>
            <div style={theme.btnRow}>
              <button type="button" style={theme.btnGhost} onClick={handleCloseModal}>
                Cancelar
              </button>
              <button type="submit" style={theme.btnPrimary} disabled={submitting}>
                {submitting ? 'Guardando...' : editingRecord ? 'Guardar' : 'Crear récord'}
              </button>
            </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
};

export default RecordsView;
